import {
  AccountingSettings, AISettings,
  ApprovalConfirmationSettings,
  ArchiveSettings,
  AuthenticationSettings,
  CaseWorkersSettings, CommentsSettings,
  CustomValueSettings,
  DialogSettings, EmployeeNumberSettings, ExportProjectLogsSettings, ExtraColumnsSettings, ForeignCurrencySettings,
  GdprSettings, ImportSettings, JournalNumberSettings, NotificationsSettings,
  OverviewXlsxSettings, PaymentReceiptsSettings,
  PaymentsSettings, ProfileSettings,
  RecommendationsSettings,
  SecuritySettings,
  Settings,
  SmsSettings, SmtpSettings,
  SupportSettings, TaxSettings,
  TransferSettings, VotesSettings, WarningsSettings
} from './settings';
import {NestedReadonly} from "../types";

export const defaultTransferSettings: TransferSettings = {
  case_documents: false,
  messages: false,
  recommendation: false
}

export const defaultAccountingSettings: AccountingSettings = {
  system: 'generic',
  export_postings: false,
  export_posting_attachments: false,
  allow_reset: false,
  batch_size: 25,
  payment_credit_account: '5678',
  payment_debit_account: '4321',
  provision_account_no: '4321',
  account_no: '1234',
  dimensions: [],
  dimension_mandatory: false,
  'file-format': 'PostingsXlsx',
  settle_exchange_rate_diff: false
}

export const defaultApprovalConfirmationSettings: ApprovalConfirmationSettings = {
  list_projects: false,
  automatic: {
    enabled: false,
    grace: 30
  }
}

export const defaultArchiveSettings: ArchiveSettings = {
  email: '',
  approvals: false,
  receipts: false,
  rejections: false
}

export const defaultAuthenticationSettings: AuthenticationSettings = {
  modes: ['mitid', 'nemid', 'codefile', 'tfa']
}


export const defaultCaseWorkersSettings: CaseWorkersSettings = {
  remove_delay: 5,
  daily_reminder: false
}

export const defaultCommentsSettings: CommentsSettings = {
  shared: false
}

export const defaultCustomValueSettings: CustomValueSettings = {
  decimals: 1
}

export const defaultDialogSettings: DialogSettings = {
  shared_reader: false,
  internal_senders: [],
  thread_type: 'applicant'
}

export const defaultEmployeeNumberSettings: EmployeeNumberSettings = {
  employeeNoRegex: ''
}

export const defaultExportProjectLogsSettings: ExportProjectLogsSettings = {
  exports_enabled: false,
  sftp_host: '',
  sftp_password: '',
  sftp_path: '/',
  sftp_user: '',
  providers: []
}

export const defaultExtraColumnsSettings: ExtraColumnsSettings = {
  fields: []
}

export const defaultGdprSettings: GdprSettings = {
  newly_approved: 1,
  newly_refused: 1,
  refused_and_moved: 30,
  old_approved: 5,
  dangling: 15,
  total_history: true,
  clear_description_on_clean: false,
  inactive_profiles: 24,
  dead_drafts: 3,
  withdrawn: 10,
  attachment_keep_on_clean: false,
  message_keep_on_clean: false,
  keep_approval_letter: false,
  keep_refusal_letter: false,
  report_keep_on_clean: false,
  purge_enabled: false,
  purge_dangling: 30,
  purge_refused: 30,
  purge_approved: 10,
  purge_log: 5,
  cleaner_disabled: false,
  clear_digest_enabled: false,
  clear_digest_period: 60
}

export const defaultNotificationsSettings: NotificationsSettings = {
  cleanup: {
    noticed: 1,
    unnoticed: 30
  }
}

export const defaultOverviewXlsxSettings: OverviewXlsxSettings = {
  columns: [
    'project.journal_no',
    'applicant.company_or_name',
    'applicant.zip',
    'applicant.country',
    'project.approved_on',
    'overview.granted_amount',
    'overview.available_start',
    'overview.granted_during',
    'overview.paid_during',
    'overview.dropped_during',
    'overview.available_stop',
    'overview.hidden_stop'
  ]
};

export const defaultPaymentsSettings: PaymentsSettings = {
  notify_accounting: '',
  notify_on_incoming: false,
  incoming_show_past: false,
  incoming_show_totals: false,
  notify_on_outgoing: false,
  outgoing_show_past: false,
  outgoing_show_totals: false,
  notify_on_dropped: false,
  dropped_show_past: false,
  dropped_show_totals: false,
  notify_on_initial_addendum: false,
  notify_on_additional_addendum: false,
  additional_addendum_show_totals: false,
  additional_addendum_show_past: false,
  notify_on_transfer: false,
  transfer_show_totals: false,
  transfer_show_past: false,
  notify_on_exchange_rate_diff: false,
  'file-format': 'PaymentsXlsx',
  notify_applicant: false,
  use_p_no: true,
  frequency_mode: 'default',
  frequency: [1, 2, 3, 4, 5],
  skip_weeks: [],
  force_future: true,
  export_cutoff: 3,
  currency_based_routing: {}
}

export const defaultPaymentReceiptsSettings: PaymentReceiptsSettings = {
  receipts_amount_check: false
}

export const defaultProfileSettings: ProfileSettings = {
  account_alert_on_payment_request: false,
  logins: ['email'],
  signups: ['email'],
  force: true,
  confirmation: false,
  allow_copy_of_rejection: false,
  allow_copy_of_approved: false,
  allow_share_applications: true,
  underage: 16,
  payment_request_notice_days: 2,
  prefer_applicant_nem_konto: false
}

export const defaultRecommendationsSettings: RecommendationsSettings = {
  warning_multiple_applications: false,
  hide_priority: true,
  attachment_scale: 1.0,
  style: 'standard',
  compact_history: 'digest',
  show_private_address: false,
  show_private_name: false,
  priorities: ['1.0', '1.1', '1.2', '1.3', '1.4', '1.5', '1.6', '1.7', '2.0', '2.1', '2.2', '2.3', '2.4', '2.5', '2.6', '2.7'],
  exceeding_grant_warning: false,
  individual_print: {
    author: false,
    amount: false,
    description: false,
    spiderweb: false,
    history: null,
    meta: ['journal_no', 'category', 'subcategory', 'applicant', 'period', 'total', 'applied']
  }
}

export const defaultSecuritySettings: SecuritySettings = {
  mail: [],
  skip_ip_check: false,
  anti_virus: {
    mail: null
  },
  hazardous_file: {
    mail: null,
    allowed_types: [],
    notifications: {
      user: false,
      profile: false
    }
  }
}

export const defaultSmsSettings: SmsSettings = {
  all_messages: false,
  approval: false,
  refusal: false,
  sender: 'GrantOne'
}

export const defaultSiblingsSettings= []


export const defaultSmtpSettings: SmtpSettings = {
  disable_deliveries: false,
  from: null,
  reply_to: null
}

export const defaultSupportSettings: SupportSettings = {
  email: 'support@grant.nu'
}

export const defaultVotesSettings: VotesSettings = {
  show_details: false
}

export const defaultImportSettings: ImportSettings = {
  show_import_project_statuses: false
}

export const defaultTaxSettings: TaxSettings = {
  report_on_past_year: false
}

export const defaultAISettings: NestedReadonly<AISettings> = {
  allow_gdpr_unsafe_files: false,
  prompt_types: []
}

export const defaultWarningsSettings: NestedReadonly<WarningsSettings> = {
  exceeding_grant_warning: false
}

export const defaultJournalNumberSettings: JournalNumberSettings = {
  cutover_day: 1,
  cutover_month: 1,
  pattern: '%<year>s-%<no>04d',
  continuous_running_number: false
}

export const defaultForeignCurrencySettings: NestedReadonly<ForeignCurrencySettings> = {
  calc_field: 'exchange_rate'
}


export const defaultSettings: NestedReadonly<Settings> = {
  accounting: {
    ...defaultAccountingSettings,
  },
  approval_confirmation: {
    ...defaultApprovalConfirmationSettings,
  },
  archive: {
    ...defaultArchiveSettings,
  },
  authentication: {
    ...defaultAuthenticationSettings,
  },
  'case-workers': {
    ...defaultCaseWorkersSettings,
  },
  'custom-value': {
    ...defaultCustomValueSettings,
  },
  dialog: {
    ...defaultDialogSettings,
  },
  gdpr: {
    ...defaultGdprSettings,
  },
  overview_xlsx: {
    ...defaultOverviewXlsxSettings,
  },
  payments: {
    ...defaultPaymentsSettings,
  },
  'payment-receipts': {
    ...defaultPaymentReceiptsSettings,
  },
  recommendations: {
    ...defaultRecommendationsSettings
  },
  security: {
    ...defaultSecuritySettings,
  },
  sms: {
    ...defaultSmsSettings,
  },
  support: {
    ...defaultSupportSettings,
  },
  'automation': {},
  'bulk_actions': {},
  'paper-refusal': {
    template: ''
  },
  'board': {
    version: 'v4',
    languages: ['da']
  },
  'related-applications': {},
  'scoring': {},
  'paper-letters': {
    'letter-types': []
  },
  'comments': {
    shared: false
  },
  notifications: {
    ...defaultNotificationsSettings
  },
  profile: {
    ...defaultProfileSettings,
  },
  'forecast': {
    no_exceeding_available: false,
    least_one_source: false,
    multiple_sources: false,
    allow_change_after_approval: false,
    dimensions: []
  },
  'languages': ['da'],
  time_zone: 'Europe/Copenhagen',
  'siblings': [],
  transfer: {
    ...defaultTransferSettings
  },
  'criteria': {
    individual: false,
    pct_precision: 1,
    allow_manual: false
  },
  materialized_view: {
    schedule: 3,
    project_list_period: 6
  },
  import: {
    ...defaultImportSettings
  },
  applicant: {
    digest: '',
    force_digest: false,
    production_unit_on_top: false
  },
  tag: '',
  votes: {
    ...defaultVotesSettings
  },
  employee_no: {
    ...defaultEmployeeNumberSettings
  },
  'tax-report': {
    ...defaultTaxSettings
  },
  ai: {
    ...defaultAISettings,
  },
  warnings: {
    ...defaultWarningsSettings
  }
};
